import type { Warning } from '@/components/common/WarningIcon'

import { formatPreciseDuration } from '@/components/VaultPage/helpers/timelock'
import { VaultV2WarningLevel } from '@/types/enums'

/** The vault's timelock warning, as `vaultV2.timelockIndicators` returns it. */
export interface VaultV2TimelocksWarning {
  level: string | null
  failedChecks: readonly { functionName: string; requiredTimelock: string }[]
}

/**
 * A queued change to one function's timelock. `functionName` is the function whose timelock
 * changes, or the raw selector when the Vaults app does not know it (which then matches no timelock row).
 */
export interface VaultV2PendingTimelockChange {
  type: 'increaseTimelock' | 'decreaseTimelock'
  functionName: string
  newDuration: string
  executableAt: number
}

export interface VaultV2TimelockEntry {
  functionName: string
  durationSeconds: number
  abdicatedAt: number | null
}

const TIMELOCK_ORDER: string[] = [
  'setIsAllocator',
  'setReceiveSharesGate',
  'setSendSharesGate',
  'setReceiveAssetsGate',
  'setSendAssetsGate',
  'setAdapterRegistry',
  'addAdapter',
  'removeAdapter',
  'increaseTimelock',
  'setPerformanceFee',
  'setManagementFee',
  'setPerformanceFeeRecipient',
  'setManagementFeeRecipient',
  'increaseAbsoluteCap',
  'increaseRelativeCap',
  'setForceDeallocatePenalty',
  'abdicate'
]

export const TIMELOCK_DESCRIPTIONS: Record<string, string> = {
  setIsAllocator:
    'Adds or removes an allocator, which can move vault liquidity between approved adapters',
  setReceiveSharesGate: 'Changes who can receive vault shares through mints or transfers',
  setSendSharesGate: 'Changes who can send vault shares through transfers or redemptions',
  setReceiveAssetsGate: 'Changes who can receive withdrawn assets from the vault',
  setSendAssetsGate: 'Changes who can deposit assets into the vault',
  setAdapterRegistry: 'Changes the registry used to validate adapters before assets can be routed',
  addAdapter: 'Enables a new adapter, expanding where lender assets can be routed',
  removeAdapter: 'Disables an adapter route, changing where vault assets may be deployed',
  increaseTimelock: 'Lengthens the timelock period for future protected vault changes',
  setPerformanceFee: 'Changes the fee charged on vault interest before lenders receive returns',
  setManagementFee: 'Changes the ongoing fee charged on vault assets',
  setPerformanceFeeRecipient: 'Changes who receives performance fees from vault interest',
  setManagementFeeRecipient: 'Changes who receives ongoing management fees from vault assets',
  increaseAbsoluteCap:
    'Raises a fixed exposure limit, allowing more lender assets to move into an allocation',
  increaseRelativeCap: 'Raises the maximum share of the vault that can sit in one allocation',
  setForceDeallocatePenalty:
    'Changes the fee used when assets are force-deallocated from an adapter',
  abdicate: 'Permanently disables a protected curator action for this vault'
}

export const VAULT_EXPOSURE_TIMELOCKS_DESCRIPTION =
  "These timelocks show how much time lenders have to review upcoming increases to vault exposure, including new adapters or higher limits for a vault's allocation strategy."

export const getTimelockDescription = (functionName: string) =>
  TIMELOCK_DESCRIPTIONS[functionName] ??
  'Controls a protected vault change that lenders may want time to review'

/**
 * Orders timelocks according to TIMELOCK_ORDER.
 * Timelocks not in the order list appear at the end.
 */
export function orderTimelocks<T extends VaultV2TimelockEntry>(timelocks: T[]) {
  const timelocksMap = new Map(timelocks.map(t => [t.functionName, t]))

  // oxlint-disable-next-line typescript/consistent-type-assertions
  const ordered = TIMELOCK_ORDER.map((name: string) => timelocksMap.get(name)).filter(
    Boolean
  ) as T[]

  const remaining = timelocks.filter(t => !TIMELOCK_ORDER.includes(t.functionName))

  return [...ordered, ...remaining]
}

/**
 * Filters pending timelock changes for a specific function name.
 */
export function getPendingTimelocksForFunction(
  pendingTimelocks: VaultV2PendingTimelockChange[],
  functionName: string
) {
  return pendingTimelocks.filter(pending => pending.functionName === functionName)
}

/**
 * Gets a warning if the timelock for a specific function is below the minimum required.
 */
export function getTimelockBelowMinimumWarning(
  warning: VaultV2TimelocksWarning,
  functionName: string
): Warning | null {
  const failedCheck = warning.failedChecks.find(check => check.functionName === functionName)
  if (!failedCheck) return null

  return {
    message: `Timelock is below the ${formatPreciseDuration(failedCheck.requiredTimelock)} minimum`,
    level: warning.level === 'red' ? VaultV2WarningLevel.Red : VaultV2WarningLevel.Yellow
  }
}
